// src/components/YouTubeLite.tsx
import { Play } from "lucide-react";
import { useState, memo } from "react";

type Props = {
  id: string;           // YouTube video ID
  title?: string;
  className?: string;
  posterQuality?: "hqdefault" | "mqdefault" | "sddefault" | "maxresdefault";
  autoplay?: boolean;   // se true, usa mute=1 para liberar autoplay cross-browser
};

/**
 * Lightweight YouTube embed sem carregar o iframe até o clique.
 * Para autoplay consistente em desktop/mobile, usamos mute=1.
 */
function YouTubeLiteBase({
  id,
  title,
  className,
  posterQuality = "hqdefault",
  autoplay = true,
}: Props) {
  const [activated, setActivated] = useState(false);
  const poster = `https://i.ytimg.com/vi/${id}/${posterQuality}.jpg`;

  // URL do embed: autoplay + mute + playsinline + rel=0 + modestbranding
  const qs = new URLSearchParams({
    autoplay: autoplay ? "1" : "0",
    mute: "0",          // <— fundamental p/ autoplay funcionar
    playsinline: "1",                     // iOS não abrir em tela cheia
    rel: "0",
    modestbranding: "1",
  }).toString();

  const src = `https://www.youtube-nocookie.com/embed/${id}?${qs}`;

  return (
    <div className={className}>
      {!activated ? (
        <button
          type="button"
          aria-label={title ? `Assistir: ${title}` : "Assistir vídeo"}
          onClick={() => setActivated(true)}
          className="group relative w-full h-full overflow-hidden rounded-xl border border-border/60 bg-muted/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        >
          <img
            src={poster}
            alt={title || "Capa do vídeo"}
            className="size-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
            loading="lazy"
            decoding="async"
          />
          {/* Gradient + botão Play */}
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-background/40 via-transparent to-transparent" />
          <div className="absolute inset-0 grid place-items-center">
            <div className="w-24 h-24 mx-auto rounded-full flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300 bg-gradient-accent text-background font-bold hover:scale-105 hover:shadow-[0_0_40px_rgba(100,192,242,0.5)] transition-all duration-300">
              <Play className="w-12 h-12 ml-1" />
            </div>
          </div>
        </button>
      ) : (
        <iframe
          className="size-full rounded-xl border border-border/60 pointer-events-auto"
          src={src}
          title={title || "YouTube video"}
          loading="lazy"
          // incluir 'autoplay' explicitamente nas permissões
          allow="autoplay; accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
          referrerPolicy="strict-origin-when-cross-origin"
        />
      )}
    </div>
  );
}

export const YouTubeLite = memo(YouTubeLiteBase);
